import { useMemo } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Clock, UserCheck, UserX } from "lucide-react";

import { api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Badge, EmptyState, PageHeader, Skeleton } from "../components/ui";
import { localDateInTimezone } from "../lib/attendance";
import { buildCheckInRows, shiftSummary } from "../lib/checkin";
import { formatClockTime } from "../lib/format";

/**
 * Check-in: the daily roster seen from the door.
 *
 * It is a join over the three existing sources -- the employee directory,
 * today's attendance register and today's published roster -- rather than a
 * fourth check-in table. Backend role scoping still decides which employees
 * each caller can see; this page only rearranges what those endpoints return.
 */
export function CheckInPage() {
  const { user } = useAuth();
  const timezone = user?.account?.timezone;
  const today = useMemo(() => localDateInTimezone(timezone), [timezone]);

  const employees = useQuery({
    queryKey: ["employee-options"],
    queryFn: ({ signal }) => api.employees(signal),
    staleTime: 5 * 60_000,
  });
  const attendance = useQuery({
    queryKey: ["attendance", "check-in", today],
    queryFn: ({ signal }) =>
      api.attendancePage({ date: today, page_size: 500 }, signal),
  });
  const shifts = useQuery({
    queryKey: ["roster", "check-in", today],
    queryFn: ({ signal }) =>
      api.shiftsPage({ from: today, to: today, page_size: 500 }, signal),
  });

  const rows = useMemo(
    () =>
      buildCheckInRows(
        employees.data ?? [],
        attendance.data?.results ?? [],
        shifts.data?.results ?? [],
        today,
      ),
    [employees.data, attendance.data, shifts.data, today],
  );

  const checkedIn = rows.filter((row) => row.checkedIn).length;
  const notCheckedIn = rows.length - checkedIn;
  const isPending = employees.isPending || attendance.isPending || shifts.isPending;
  const error = employees.error || attendance.error || shifts.error;

  return (
    <>
      <PageHeader
        title="Check-in"
        subtitle="Who is in today, when they clocked in, and the shift they are on."
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <SummaryCard label="Employees" value={rows.length} icon={<UserCheck size={16} />} />
        <SummaryCard
          label="Checked in"
          value={checkedIn}
          icon={<UserCheck size={16} />}
          tone="emerald"
        />
        <SummaryCard
          label="Not checked in"
          value={notCheckedIn}
          icon={<UserX size={16} />}
          tone="rose"
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        {isPending ? (
          <Skeleton className="h-56 w-full" />
        ) : error ? (
          <EmptyState
            title="Could not load check-ins"
            body="Please refresh the page and try again."
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No employees"
            body="Employees added to this company appear here on the day they are expected."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5">Employee</th>
                  <th className="px-4 py-2.5">Employee ID</th>
                  <th className="px-4 py-2.5">Check-in status</th>
                  <th className="px-4 py-2.5">Check-in time</th>
                  <th className="px-4 py-2.5">Assigned shift</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr
                    key={row.employee.id}
                    className={
                      row.checkedIn
                        ? "border-l-4 border-l-emerald-400 bg-emerald-50/60"
                        : "border-l-4 border-l-transparent hover:bg-slate-50"
                    }
                  >
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/people/employees/${row.employee.id}`}
                        className="text-sm font-medium text-slate-900 hover:underline"
                      >
                        {row.employee.display_name}
                      </Link>
                      <p className="text-xs text-slate-500">
                        {[row.employee.designation, row.employee.department]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </p>
                    </td>
                    <td className="px-4 py-2.5 text-sm tabular-nums text-slate-600">
                      {row.employee.employee_code || "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge tone={row.checkedIn ? "emerald" : "rose"}>
                        {row.checkedIn ? "Checked in" : "Not checked in"}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-slate-600">
                      {row.attendance?.clock_in_at
                        ? formatClockTime(row.attendance.clock_in_at, timezone)
                        : "—"}
                      {row.attendance?.clock_out_at ? (
                        <p className="text-xs text-slate-400">
                          Out {formatClockTime(row.attendance.clock_out_at, timezone)}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-slate-600">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock size={14} className="shrink-0 text-slate-400" />
                        {shiftSummary(row.shift)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function SummaryCard({
  label,
  value,
  icon,
  tone = "slate",
}: {
  label: string;
  value: number;
  icon: ReactNode;
  tone?: "slate" | "emerald" | "rose";
}) {
  const toneClass =
    tone === "emerald"
      ? "text-emerald-600 bg-emerald-50 ring-emerald-600/20"
      : tone === "rose"
        ? "text-rose-600 bg-rose-50 ring-rose-600/20"
        : "text-slate-600 bg-slate-50 ring-slate-500/20";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-xs">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
          {label}
        </span>
        <span className={`grid h-7 w-7 place-items-center rounded-full ring-1 ring-inset ${toneClass}`}>
          {icon}
        </span>
      </div>
      <p className="mt-2 text-2xl font-semibold tabular-nums text-slate-900">{value}</p>
    </div>
  );
}
